import type { Palette, Theme } from './theme.js';

/**
 * Built-in themes.
 *
 * The colour choices here are not taste — the categorical `chart` arrays are the
 * validated eight-slot palette (fixed slot order is the colour-blind-safety
 * mechanism, so never re-order or cycle them), checked with the palette
 * validator against these exact surfaces:
 *
 *   light (#fcfcfb): CVD ΔE 9.1, normal-vision ΔE 19.6 — PASS
 *                    3 slots (aqua/yellow/magenta) sit below 3:1 on the light
 *                    surface, so every chart ships direct labels + a table view
 *                    toggle as the required relief channel.
 *   dark  (#1a1a19): CVD ΔE 8.4, normal-vision ΔE 19.3, contrast — all PASS
 *
 * Health and severity colours come from the reserved status scale and are
 * deliberately distinct steps from the categorical slots, so a status colour can
 * never impersonate a series. They are always rendered with an icon + text
 * label, never colour alone.
 */

const lightPalette: Palette = {
  bg: '#f9f9f7',
  surface: '#fcfcfb',
  surfaceRaised: '#ffffff',
  surfaceSunken: '#f0efec',
  overlay: '#ffffff',
  border: '#e1e0d9',
  borderStrong: '#c3c2b7',

  text: '#0b0b0b',
  textMuted: '#52514e',
  textSubtle: '#898781',
  textInverted: '#ffffff',

  primary: '#2a78d6',
  primaryHover: '#256abf',
  primaryText: '#ffffff',
  accent: '#3987e5',

  // reserved status scale — never reused as a series colour
  up: '#0ca30c',
  degraded: '#fab219',
  down: '#d03b3b',
  maintenance: '#52514e',
  unknown: '#898781',

  info: '#256abf',
  warning: '#fab219',
  critical: '#d03b3b',
  success: '#006300',

  chart: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
};

const darkPalette: Palette = {
  bg: '#0d0d0d',
  surface: '#1a1a19',
  surfaceRaised: '#232322',
  surfaceSunken: '#131312',
  overlay: '#232322',
  border: '#2c2c2a',
  borderStrong: '#383835',

  text: '#ffffff',
  textMuted: '#c3c2b7',
  textSubtle: '#898781',
  textInverted: '#0b0b0b',

  primary: '#3987e5',
  primaryHover: '#5598e7',
  primaryText: '#0b0b0b',
  accent: '#5598e7',

  up: '#0ca30c',
  degraded: '#fab219',
  down: '#d03b3b',
  maintenance: '#c3c2b7',
  unknown: '#898781',

  info: '#5598e7',
  warning: '#fab219',
  critical: '#d03b3b',
  success: '#0ca30c',

  // same eight hues, re-stepped for the dark surface — not an automatic flip
  chart: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
};

const now = 0; // stamped at install time by the migration

export const BUILTIN_THEMES: Theme[] = [
  {
    id: 'default',
    name: 'Default',
    builtin: true,
    customerId: null,
    isDefault: true,
    light: lightPalette,
    dark: darkPalette,
    branding: {
      productName: 'IT Resources',
      logoUrl: null,
      logoDarkUrl: null,
      faviconUrl: null,
      tagline: 'Infrastructure, in one place.',
      supportEmail: null,
      supportPhone: null,
    },
    typography: {
      fontSans: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
      fontMono: "'JetBrains Mono', ui-monospace, 'Cascadia Code', monospace",
      scale: 1,
    },
    shape: { radius: 10, density: 'comfortable', shadows: true },
    accessibility: { highContrast: false, colorBlindSafe: false, reduceMotion: false },
    createdAt: now,
    updatedAt: now,
  },
  {
    /**
     * For wall-mounted NOC displays: read from 4 metres away. Bigger type, no
     * shadows (they wash out on projectors), thicker borders, compact rows so
     * more resources fit, and shape encoding on top of colour.
     */
    id: 'noc-wall',
    name: 'NOC Wall',
    builtin: true,
    customerId: null,
    isDefault: false,
    light: { ...lightPalette, border: '#c3c2b7', borderStrong: '#898781' },
    dark: { ...darkPalette, bg: '#000000', surface: '#111111', border: '#383835', borderStrong: '#52514e' },
    branding: {
      productName: 'IT Resources NOC',
      logoUrl: null,
      logoDarkUrl: null,
      faviconUrl: null,
      tagline: '',
      supportEmail: null,
      supportPhone: null,
    },
    typography: {
      fontSans: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
      fontMono: "'JetBrains Mono', ui-monospace, 'Cascadia Code', monospace",
      scale: 1.3,
    },
    shape: { radius: 6, density: 'compact', shadows: false },
    accessibility: { highContrast: true, colorBlindSafe: true, reduceMotion: true },
    createdAt: now,
    updatedAt: now,
  },
];

export const DEFAULT_THEME_ID = 'default';
